import React from "react";
import { Download, FileText } from "lucide-react";
import type { Order, OrderAttachment, OrderStatus } from "../types";
import { ORDER_STATUS_LABELS, ORDER_OPTION_TYPE_LABELS, getStatusBadgeColor } from "../constants";
import { OrderRefundCard } from "./OrderRefundCard";

interface OrderDetailProps {
  companyId: number;
  order: Order;
  onStatusChange: (action: string) => void;
}

/** 내려받기 링크 하나. 여러 파일이면 첫 파일에 '대표' 표시를 붙인다 (생산 매칭에 쓰이는 파일) */
const AttachmentLink: React.FC<{ url: string | null; fileName: string; isPrimary: boolean }> = ({
  url,
  fileName,
  isPrimary,
}) => {
  const body = (
    <>
      <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex items-center gap-2">
        <span className="text-sm text-gray-900 truncate text-left">{fileName}</span>
        {isPrimary && (
          <span className="px-1.5 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#E8EFFF] text-[#4285F4] flex-shrink-0">
            대표
          </span>
        )}
      </div>
      <Download className="w-5 h-5 text-gray-400 flex-shrink-0" />
    </>
  );
  const className =
    "flex items-center gap-3 p-4 bg-gray-50 rounded-[0.625rem] border border-gray-200";
  if (!url) {
    return <div className={className}>{body}</div>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      download={fileName}
      className={`${className} hover:bg-gray-100 transition-colors`}
    >
      {body}
    </a>
  );
};

export const OrderDetail: React.FC<OrderDetailProps> = ({
  companyId,
  order,
  onStatusChange,
}) => {
  // 여러 파일을 올린 주문은 attachments 에 모두 있고, 예전 주문은 대표 파일(files) 하나만 있다
  const attachmentList: OrderAttachment[] =
    order.attachments && order.attachments.length > 0
      ? order.attachments
      : order.files
        ? [{ url: order.files, fileName: order.files.split("?")[0].split("/").pop() ?? "파일" }]
        : [];

  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="w-full">
        {/* 헤더 - 타이틀과 액션 */}
        <div className="flex items-center justify-between mb-[28px]">
          <div className="flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] font-semibold">{order.productName}</h2>
            <span className="text-[1rem] font-normal text-gray-600">
              주문 번호: {order.orderNumber}
            </span>
          </div>

          {/* 상태 변경 드롭다운 */}
          <div className="relative">
            <select
              className="inline-flex items-center justify-center font-medium rounded-[8px]
              transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
              bg-blue-600 text-white hover:bg-blue-700
              px-4 py-2 text-sm gap-2 appearance-none cursor-pointer pr-10"
              value=""
              onChange={(e) => {
                const action = e.target.value;
                if (action) {
                  onStatusChange(action);
                  e.target.value = "";
                }
              }}
            >
              <option value="">주문 상태 변경</option>
              <option value="pending_payment">결제전</option>
              <option value="accepted">접수</option>
              <option value="resubmission">재접수</option>
              <option value="production">제작</option>
              <option value="production_complete">제작 완료</option>
              <option value="shipping">출고/배송</option>
              <option value="delivered">배송 완료</option>
              <option value="cancelled">취소</option>
              <option value="refunded">환불</option>
            </select>

            {/* 드롭다운 화살표 아이콘 */}
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* 주문 정보와 고객 정보 카드 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* 주문 정보 카드 */}
          <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-left">
              주문 정보
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">주문번호</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.orderNumber}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">주문일</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.orderDate}{order.isLegacyDate ? " (기존 생성일)" : ""}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">상태</span>
                <span
                  className={`inline-flex px-3 py-1 text-xs font-medium rounded-full ${getStatusBadgeColor(order.status)}`}
                >
                  {ORDER_STATUS_LABELS[order.status]}
                </span>
              </div>
            </div>
          </div>

          {/* 고객 정보 카드 */}
          <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-left">
              고객 정보
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">이름</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.customerName}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">이메일</span>
                <span className="text-sm font-medium text-gray-900">
                  {order.customerEmail}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 상품 정보 카드 */}
        <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-6 text-left">
            상품 정보
          </h3>

          {/* 상품 영역 */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="text-left">
                <div className="text-base font-semibold text-gray-900">
                  {order.productName}
                </div>
                {order.workTitle && (
                  <div className="mt-1 text-sm text-gray-600">
                    작업물 제목: <span className="font-medium text-gray-900">{order.workTitle}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-8 text-sm">
                <div>
                  <span className="text-gray-600">수량: </span>
                  <span className="font-medium text-gray-900">
                    {order.quantity}개
                  </span>
                </div>
                {order.totalAmount > 0 && (
                  <>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <div>
                      <span className="text-gray-600">단가: </span>
                      <span className="font-medium text-gray-900">
                        ₩{order.unitPrice.toLocaleString()}
                      </span>
                    </div>
                    <div className="w-px h-4 bg-gray-300"></div>
                    <div>
                      <span className="text-gray-600">소계: </span>
                      <span className="font-medium text-gray-900">
                        ₩{(order.unitPrice * order.quantity).toLocaleString()}
                      </span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 옵션 영역 */}
          {order.options && order.options.length > 0 ? (
            <div className="border-t border-gray-200 pt-6">
              <div className="text-sm font-medium text-gray-600 mb-4 text-left">
                선택 옵션
              </div>
              <div className="space-y-3">
                {order.options.map((opt, idx) => (
                  <div key={idx} className="bg-gray-50 rounded-[0.625rem] p-4">
                    <div className={`flex items-center justify-between ${order.totalAmount > 0 ? 'mb-3 pb-3 border-b border-gray-200' : ''}`}>
                      <div className="flex items-center gap-2">
                        <span className="px-2 py-0.5 text-[11px] font-medium rounded-[4px] bg-[#E8EFFF] text-[#4285F4]">
                          {ORDER_OPTION_TYPE_LABELS[opt.optionName] || opt.optionName}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {opt.optionValue}
                        </span>
                      </div>
                    </div>
                    {order.totalAmount > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-6">
                          <div>
                            <span className="text-gray-600">수량: </span>
                            <span className="font-medium text-gray-900">
                              {opt.optionCount}개
                            </span>
                          </div>
                          <div className="w-px h-4 bg-gray-300"></div>
                          <div>
                            <span className="text-gray-600">단가: </span>
                            <span className="font-medium text-gray-900">
                              ₩{opt.optionPrice.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-600">소계: </span>
                          <span className="font-medium text-gray-900">
                            ₩{opt.optionTotalPrice.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* 총액 */}
          {order.totalAmount > 0 && (
            <div className="border-t border-gray-200 mt-6 pt-6">
              <div className="flex items-center justify-between px-4">
                <span className="text-sm text-gray-600">총 금액</span>
                <span className="text-lg font-bold text-blue-600">
                  ₩{order.totalAmount.toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* 환불 내역 카드 — 무통장입금 주문만. 결제수단을 모르면 서버 판단(notAllowedReason)에 맡긴다 */}
        {order.paymentMethod !== "TOSS" && Number.isFinite(companyId) && (
          <OrderRefundCard companyId={companyId} orderId={Number(order.id)} />
        )}

        {/* 첨부 파일 카드 */}
        <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4 text-left">
            첨부 파일
          </h3>
          {attachmentList.length > 0 ? (
            <div className="space-y-2">
              {attachmentList.map((file, index) => (
                <AttachmentLink
                  key={`${file.fileName}-${index}`}
                  url={file.url}
                  fileName={file.fileName}
                  isPrimary={attachmentList.length > 1 && index === 0}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-[0.625rem] border border-gray-200">
              <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <span className="text-sm text-gray-500">
                첨부된 파일이 없습니다.
              </span>
            </div>
          )}
        </div>

        {/* 메모 카드 */}
        {order.memo && (
          <div className="bg-white border border-gray-200 rounded-[0.625rem] p-6 shadow-sm mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 text-left">
              메모 · 고객 비고
            </h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap text-left">
              {order.memo}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

